import { Link } from "wouter";
import { ArrowRight, Check, MapPin, Clock, Truck, Shield, Zap, Target } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead, { createLocalBusinessSchema } from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import PageHero from "@/components/PageHero";
import { IMAGES, CONTACT } from "@/lib/constants";
import LazyImage from "@/components/LazyImage";

const localBenefits = [
  {
    icon: Truck,
    title: "Snabb leverans till Katrineholm",
    text: "Vi planerar leveransen från Eskilstuna till Katrineholm utifrån detaljernas omfattning, önskat datum och vald transportlösning.",
  },
  {
    icon: Clock,
    title: "Korta ledtider",
    text: "Tack vare tre CO2-lasrar och hög kapacitet kan vi hantera brådskande beställningar från Katrineholmsregionen med kort varsel.",
  },
  {
    icon: Shield,
    title: "Garanterad precision",
    text: "Toleranser ner till 0,1 mm med moderna Trumpf- och Amada CO2-lasrar. Första bit-kontroll vid varje projektstart.",
  },
  {
    icon: Target,
    title: "Helhetslösning",
    text: "Kombinera laserskärning med kantpressning och svetsning – allt under ett tak. Färdiga komponenter levererade till Katrineholm.",
  },
];

const katrineholmIndustries = [
  {
    name: "Logistik & Transport",
    desc: "Som en logistikhub är Katrineholm hem för många transportföretag. Vi levererar slitstarka komponenter till fordon och terminalutrustning.",
  },
  {
    name: "Tillverkningsindustri",
    desc: "Prototyper och serieproduktion för tillverkande företag i Katrineholmsområdet. Från enstaka detaljer till stora volymer för maskinbyggare.",
  },
  {
    name: "Livsmedelsindustri",
    desc: "Rostfria och syrafasta detaljer för livsmedelsanläggningar med höga krav på hygien och hållbarhet, anpassade för Katrineholms producenter.",
  },
  {
    name: "Bygg & Entreprenad",
    desc: "Skräddarsydda metalldetaljer för lokala byggprojekt – från konstruktionsstål till designelement för fastigheter i Katrineholm.",
  },
];

const capabilities = [
  { material: "Svart stål", maxThickness: "25 mm", minThickness: "0,5 mm" },
  { material: "Rostfritt stål", maxThickness: "20 mm", minThickness: "0,5 mm" },
  { material: "Aluminium", maxThickness: "15 mm", minThickness: "0,5 mm" },
  { material: "Höghållfasthetsstål (Strenx)", maxThickness: "20 mm", minThickness: "1 mm" },
  { material: "Slitstål (Hardox/Raex)", maxThickness: "20 mm", minThickness: "1 mm" },
];

export default function LaserskarningKatrineholm() {
  const localServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Laserskärning Katrineholm",
    description:
      "Professionell laserskärning för företag i Katrineholm och Södermanland. ASPAB erbjuder CO2 Laser-skärning med toleranser ner till 0,1 mm, snabba leveranser och brett materialutbud.",
    provider: {
      "@type": "LocalBusiness",
      name: "ASPAB",
      telephone: "+46161326 00",
      email: "order@aspab.com",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Abramsons väg 2",
        addressLocality: "Eskilstuna",
        postalCode: "632 39",
        addressCountry: "SE",
      },
    },
    areaServed: {
      "@type": "City",
      name: "Katrineholm",
      containedInPlace: {
        "@type": "Country",
        name: "Sweden",
      },
    },
    serviceType: "Laserskärning",
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Laserskärningstjänster",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "CO2 Laser-skärning stål",
            description: "Laserskärning i svart stål upp till 25 mm tjocklek",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "CO2 Laser-skärning rostfritt",
            description: "Laserskärning i rostfritt stål upp till 20 mm tjocklek",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "CO2 Laser-skärning aluminium",
            description: "Laserskärning i aluminium upp till 15 mm tjocklek",
          },
        },
      ],
    },
  };

  const localBusinessSchema = createLocalBusinessSchema({
    areaServed: "Katrineholm",
    serviceDescription: "Professionell laserskärning, kantpressning och svetsning för företag i Katrineholm och Södermanland. Leveransplan bekräftas i offerten.",
  });

  return (
    <Layout>
      <SEOHead
        title="Laserskärning Katrineholm – Lokal leverantör | ASPAB"
        description="Behöver du laserskärning i Katrineholm? ASPAB levererar med 0,1 mm tolerans. Snabb leverans från Eskilstuna. Stål, rostfritt & aluminium. Begär offert!"
        canonical="https://aspab.com/laserskarning-katrineholm"
        path="/laserskarning-katrineholm"
        keywords="laserskärning Katrineholm, laserskärning Sörmland, plåtbearbetning Katrineholm, CO2 Laser Katrineholm, metallbearbetning Katrineholm"
        breadcrumbs={[
          { name: "Laserskärning", href: "/tjanster/laserskarning" },
          { name: "Laserskärning Katrineholm", href: "/laserskarning-katrineholm" },
        ]}
        structuredData={[localServiceSchema, localBusinessSchema]}
        ogImage={IMAGES.ogHem}
      />

      <PageHero
        title="Laserskärning Katrineholm"
        subtitle="Din regionala partner för laserskärning i Katrineholm med precision, kvalitet och planerad leverans från ASPAB i Eskilstuna."
        breadcrumbs={[
          { name: "Laserskärning", href: "/tjanster/laserskarning" },
          { name: "Laserskärning Katrineholm", href: "/laserskarning-katrineholm" },
        ]}
        image={IMAGES.heroLaser}
      />

      {/* Intro – Varför välja ASPAB från Katrineholm */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                  Laserskärning för Katrineholmsregionen
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Din partner för laserskärning i Katrineholm
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  Behöver ditt företag i Katrineholm en pålitlig leverantör av laserskurna detaljer? ASPAB i
                  Eskilstuna erbjuder snabb och precis laserskärning med leverans till hela Södermanland.
                  Med planerade transporter från Eskilstuna får du en smidig leveranslösning och kapaciteten hos en specialiserad laserskärningsverkstad.
                </p>
                <p className="text-gray-500 mt-4 leading-relaxed">
                  Vi är en etablerad partner till industrin i Katrineholm, från logistikföretag till tillverkningsindustrin.
                  Oavsett om du behöver prototyper, enstaka detaljer eller stora serier levererar vi med precision och korta ledtider.
                </p>
                <div className="flex items-center gap-3 mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <MapPin className="w-5 h-5 text-[#2563EB] shrink-0" />
                  <div>
                    <span className="text-gray-900 font-medium text-sm">Leverans från ASPAB i Eskilstuna till Katrineholm</span>
                    <span className="text-gray-500 text-sm block">Via väg 214 – snabb och smidig leverans</span>
                  </div>
                </div>
                <Link
                  href="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#2563EB] text-white font-semibold rounded-md hover:bg-[#1D4ED8] transition-colors mt-6"
                >
                  Begär offert – fri leveransbedömning <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <div className="space-y-4">
                <LazyImage
                  src={IMAGES.laserOldSite}
                  alt="Laserskärning för företag i Katrineholm – ASPAB CO2 Laser"
                  className="w-full rounded-lg"
                />
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">0,1 mm</div>
                    <div className="text-gray-500 text-sm mt-1">Tolerans</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">&lt;24h</div>
                    <div className="text-gray-500 text-sm mt-1">Leverans enkla detaljer</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">25 mm</div>
                    <div className="text-gray-500 text-sm mt-1">Max tjocklek stål</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">30+</div>
                    <div className="text-gray-500 text-sm mt-1">Års erfarenhet</div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Fördelar med ASPAB för Katrineholmsföretag */}
      <section className="bg-gray-50 py-20 md:py-28 border-t border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-2xl mb-14">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                Fördelar
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                Varför företag i Katrineholm väljer ASPAB
              </h2>
              <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                Närhet, kapacitet och specialistkunskap – vi kombinerar fördelarna med en lokal verkstad med
                musklerna hos en av Mälardalens ledande specialister på laserskärning.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {localBenefits.map((item, i) => (
              <AnimatedSection key={i} delay={i * 100}>
                <div className="bg-white p-6 rounded-lg border border-gray-200 h-full">
                  <item.icon className="w-8 h-8 text-[#2563EB] mb-4" />
                  <h3 className="font-heading text-lg font-bold text-gray-900">{item.title}</h3>
                  <p className="text-gray-500 mt-2 text-sm">{item.text}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Industrier i Katrineholm */}
      <section className="bg-white py-20 md:py-28 border-t">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                  Lokala industrier
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Laserskärning för Katrineholms näringsliv
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  Från den starka logistiksektorn till tillverkningsindustrin – ASPAB är en
                  stolt leverantör till Katrineholms dynamiska näringsliv. Vi förstår de lokala
                  behoven och levererar anpassade lösningar med hög precision.
                </p>
                <div className="mt-8 space-y-6">
                  {katrineholmIndustries.map((industry, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                        <Zap className="w-5 h-5 text-[#2563EB]" />
                      </div>
                      <div>
                        <h4 className="font-heading font-semibold text-gray-900">{industry.name}</h4>
                        <p className="text-gray-500 text-sm mt-1">{industry.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <LazyImage
                src={IMAGES.workshop3}
                alt="Industriell laserskärning för Katrineholms företag"
                className="w-full rounded-lg"
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Kapabiliteter Tabell */}
      <section className="bg-gray-50 py-20 md:py-28 border-t">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-2xl mb-14 mx-auto text-center">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                Kapacitet & Material
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                CO2-laserskärning för en rad material
              </h2>
              <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                Våra tre CO2-lasermaskiner från Trumpf och Amada hanterar allt från tunnplåt till 25 mm tjockt stål
                med bibehållen precision. Vi har ett stort lager av de vanligaste materialen för snabba omställningar.
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={200}>
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900">Material</th>
                    <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900">Max tjocklek</th>
                    <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900">Min tjocklek</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {capabilities.map((cap, i) => (
                    <tr key={i}>
                      <td className="whitespace-nowrap px-4 py-3 font-medium text-gray-900">{cap.material}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-gray-700">{cap.maxThickness}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-gray-700">{cap.minThickness}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-center mt-8">
              <p className="text-gray-500 mb-4">Behöver du hjälp med andra material eller tjocklekar? Kontakta oss för en bedömning.</p>
              <Link
                href="/kontakt"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gray-900 font-semibold rounded-md border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                Kontakta oss <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-20 md:py-28 border-t">
        <div className="container text-center">
          <AnimatedSection>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900">
              Redo att starta ditt projekt i Katrineholm?
            </h2>
            <p className="text-gray-500 mt-4 text-lg max-w-2xl mx-auto">
              Skicka in dina ritningar (DXF/DWG/STEP) så återkommer vi med en kostnadsfri offert,
              så snart underlaget är granskat. Vi ser fram emot att bli din partner för laserskärning.
            </p>
            <Link
              href={"/kontakt"}
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#2563EB] text-white font-bold rounded-md hover:bg-[#1D4ED8] transition-colors mt-8 text-lg"
            >
              Begär offert nu <ArrowRight className="w-5 h-5" />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
